export type RecipeCondition = {
  id: string;
  label?: string;
  description?: string;
  group?: "core" | "filter";
  required?: boolean;
  enabled: boolean;
};

export type RecipeGroup = {
  id: "core" | "filter";
  label: string;
  description: string;
  selection_mode: string;
  conditions: RecipeCondition[];
};

export type RecipePreview = {
  recipe_id: string;
  recipe_version: number;
  label: string;
  description: string;
  requires_option_contract: boolean;
  selection_mode: string;
  min_enabled: number;
  core_group_mode: string;
  filter_group_mode: string;
  conditions: RecipeCondition[];
  core_conditions: RecipeCondition[];
  filter_conditions: RecipeCondition[];
  groups: RecipeGroup[];
};

export type IntentConfig = {
  intent: string;
  label: string;
  description: string;
  requires_option_contract: boolean;
  recipe_id: string;
  recipe_version: number;
};

export interface WatchIntentDefinition {
  intent: string;
  label: string;
  description?: string;
  tooltip?: string;
  enabled?: boolean;
  requires_option_contract?: boolean;
  default_alert_mode?: "one_and_done" | "keep_watching";
  default_intent_params?: Record<string, unknown>;
  default_policy?: {
    max_duration_days: number;
    evaluation_interval_minutes: number;
    max_alerts_per_watch: number;
    cooldown_minutes: number;
    market_hours_only: boolean;
  };
}

export interface WatchContractsPayload {
  /** Additive, runtime-decoded versioned signal catalog. */
  signal_catalog?: unknown;
  /** Additive, runtime-decoded deterministic recipe catalog. */
  recipe_catalog?: unknown;
  /** Additive, fail-closed exact option-condition authoring capability. */
  option_evidence_clauses?: unknown;
  enabled_intents?: string[];
  intent_definitions?: WatchIntentDefinition[];
  authoring?: {
    schema_version?: number;
    goals?: {
      position?: { enabled?: boolean; subject?: string };
      price_target?: { enabled?: boolean; subject?: string; unit?: "USD_PER_SHARE" };
      structure_entry?: { enabled?: boolean; subject?: string; unit?: "USD_PER_SHARE" };
    };
  };
  /**
   * Additive authoring contract. Schema v1 remains readable while clients
   * migrate, but only v2 is authoritative for per-structure availability.
   */
  authoring_v2?: {
    schema_version?: number;
    goals?: {
      position?: WatchAuthoringGoalCapability;
      price_target?: WatchMarketGoalCapability;
      structure_entry?: WatchAuthoringGoalCapability;
    };
  };
  feature_flags?: {
    watch_structure_entry_enabled?: boolean;
    watch_single_entry_mode?: "DISABLED" | "SHADOW" | "NOTIFY" | string;
  };
}

export type WatchAuthoringStructureId = "single" | "vertical" | "iron_condor" | "iron_butterfly";

export interface WatchAuthoringStructureCapability {
  enabled?: boolean;
  /** True only when an ordinary user can receive the advertised alert. */
  customer_visible?: boolean;
  rollout_mode?: "DISABLED" | "SHADOW" | "NOTIFY" | string;
  option_types?: Array<"CALL" | "PUT" | string>;
  sides?: Array<"LONG" | "SHORT" | string>;
  target_types?: Array<"NET_DEBIT_LTE" | "NET_CREDIT_GTE" | string>;
  unit?: "USD_PER_SHARE" | string;
}

export interface WatchAuthoringGoalCapability {
  enabled?: boolean;
  subject?: string;
  structures?: Partial<Record<WatchAuthoringStructureId, WatchAuthoringStructureCapability>>;
  trigger_types?: string[];
  trigger_logic?: string[];
}

export interface WatchMarketGoalCapability {
  enabled?: boolean;
  subject?: string;
  trigger_types?: string[];
  operators?: string[];
  unit?: "USD_PER_SHARE" | string;
}

export type AlertMode = "one_and_done" | "keep_watching";
export type ContractMode = "builder" | "manual";
export type ContractType = "call" | "put";
export type TargetDirection = "above" | "below";
export type VolatilitySensitivity = "low" | "medium" | "high";

export const FALLBACK_INTENT_DEFINITIONS: WatchIntentDefinition[] = [
  {
    intent: "good_time_to_buy",
    label: "Good Time to Buy",
    enabled: true,
    requires_option_contract: true,
  },
  {
    intent: "good_time_to_sell",
    label: "Good Time to Sell",
    enabled: true,
    requires_option_contract: true,
  },
  {
    intent: "price_target",
    label: "Price Target",
    enabled: true,
    requires_option_contract: false,
  },
];
